"use client";

import { SolicitudPolizaV3, Comentario, FechaLike, SeleccionAgente, PolizaDetectadaFast360 } from "@/types/solicitud";
import { isPreClasico } from "@/lib/preClasicos";

// ── Documentación del cliente (obligatoria) ─────────────────────

export const TIPO_DOC_CARNET_ANVERSO =
  "Carnet de Conducir (Anverso)" as const;
export const TIPO_DOC_CARNET_REVERSO =
  "Carnet de Conducir (Reverso)" as const;
/** Tipo legacy (antes de anverso/reverso); cuenta como anverso. */
export const TIPO_DOC_CARNET_LEGACY = "Carnet de Conducir" as const;

export const DOCS_CLIENTE_BASE = [
  "Permiso de Circulación (Clásico)",
  "Ficha Técnica (Clásico)",
  TIPO_DOC_CARNET_ANVERSO,
  TIPO_DOC_CARNET_REVERSO,
  "Permiso de Circulación (Actual)",
] as const;

/** True si el tipo documental satisface el slot de anverso del carnet. */
export function matchesCarnetAnverso(tipo?: string): boolean {
  return (
    tipo === TIPO_DOC_CARNET_ANVERSO || tipo === TIPO_DOC_CARNET_LEGACY
  );
}

/** True si el item encaja con el slot requerido (legacy carnet → anverso). */
export function matchesRequiredDocType(
  itemTipo: string | undefined,
  required: string,
): boolean {
  if (required === TIPO_DOC_CARNET_ANVERSO) {
    return matchesCarnetAnverso(itemTipo);
  }
  return itemTipo === required;
}

export const DOCS_CLIENTE_PRE_CLASICOS_EXTRA = [
  "Póliza de seguro (Actual)",
  "Recibo de seguro (Actual)",
] as const;

/** Tipo de documento interno (solo agente, sin reclamación al cliente). */
export const TIPO_DOC_CERTIFICADO_CLUB_CLASICOS =
  "Certificado de Club Clásicos" as const;

/** Documentación interna obligatoria en Pre-Clásicos (no cuenta para reclamación). */
export const DOCS_INTERNOS_PRE_CLASICOS = [
  TIPO_DOC_CERTIFICADO_CLUB_CLASICOS,
] as const;

export const TIPOS_POLIZA_EMITIDA = ["Póliza RC", "Póliza Asistencia"] as const;

/** True si el ERP Fast360 quedó sincronizado (synced o Fase B OK sin sync parcial). */
export function isFast360Synced(fast360?: SolicitudPolizaV3["fast360"]): boolean {
  if (!fast360) return false;
  if (fast360.synced) return true;
  return !!fast360.faseB_ok && !fast360.partialSync;
}

/**
 * Reconoce el bloqueo del prechequeo Fast360 (la matrícula ya tiene una
 * póliza viva en el ERP) entre los demás errores de la transición.
 * El backend lo marca en `details.motivo` para no depender del texto.
 */
export function esBloqueoMatriculaAsegurada(error: unknown): boolean {
  const details = (error as { details?: { motivo?: string } } | null)?.details;
  return details?.motivo === "MATRICULA_ASEGURADA";
}

/** Pólizas que el backend adjunta al bloqueo, para enseñarlas al agente. */
export function polizasDelBloqueo(
  error: unknown,
): PolizaDetectadaFast360[] {
  const details = (error as {
    details?: { polizas?: PolizaDetectadaFast360[] };
  } | null)?.details;
  return Array.isArray(details?.polizas) ? details.polizas : [];
}

export type DocEmbudoMode = "clasicos" | "pre-clasicos";

export function getClientDocsRequired(
  solicitud: SolicitudPolizaV3,
  mode?: DocEmbudoMode
): string[] {
  const esPre =
    mode === "pre-clasicos" ||
    solicitud.tags?.includes("PRE_CLASICO") ||
    isPreClasico(solicitud as Parameters<typeof isPreClasico>[0]);
  return esPre
    ? [...DOCS_CLIENTE_BASE, ...DOCS_CLIENTE_PRE_CLASICOS_EXTRA]
    : [...DOCS_CLIENTE_BASE];
}

/** Documentos internos obligatorios en Pre-Clásicos (solo agente). */
export function getInternalDocsRequired(
  solicitud: SolicitudPolizaV3,
  mode?: DocEmbudoMode
): string[] {
  const esPre =
    mode === "pre-clasicos" ||
    solicitud.tags?.includes("PRE_CLASICO") ||
    isPreClasico(solicitud as Parameters<typeof isPreClasico>[0]);
  return esPre ? [...DOCS_INTERNOS_PRE_CLASICOS] : [];
}

export interface ClientDocProgress {
  done: number;
  total: number;
  hasIssue: boolean;
  isComplete: boolean;
}

/**
 * Progreso SOLO de la documentación del cliente (excluye pólizas emitidas por bots).
 * - done: slots obligatorios validados por IA o manualmente
 * - total: siempre 5 (clásicos) o 7 (pre-clásicos) — carnet anverso+reverso
 * - hasIssue: algún doc del cliente rechazado por IA
 * - isComplete: todos los slots están validados
 */
export function getClientDocProgress(
  solicitud: SolicitudPolizaV3,
  mode?: DocEmbudoMode
): ClientDocProgress {
  const required = getClientDocsRequired(solicitud, mode);
  const items = (solicitud.documentacion?.items || []).filter(
    (d) => !TIPOS_POLIZA_EMITIDA.includes(d.tipoDocumento as (typeof TIPOS_POLIZA_EMITIDA)[number])
  );

  let done = 0;
  let hasIssue = false;

  for (const req of required) {
    const doc = items.find((d) => matchesRequiredDocType(d.tipoDocumento, req));
    if (doc) {
      const validado = doc.estadoIA === "VALIDO" ||
        Boolean(doc.validadoManualmentePor);
      if (validado) done++;
      if (doc.estadoIA === "INVALIDO") hasIssue = true;
    }
  }

  return {
    done,
    total: required.length,
    hasIssue,
    isComplete: done === required.length,
  };
}

export interface InternalDocProgress {
  done: number;
  total: number;
  isComplete: boolean;
}

/**
 * Progreso de documentación interna (solo agente, p. ej. Certificado Club).
 * No cuenta para reclamaciones al cliente.
 */
export function getInternalDocProgress(
  solicitud: SolicitudPolizaV3,
  mode?: DocEmbudoMode
): InternalDocProgress {
  const required = getInternalDocsRequired(solicitud, mode);
  if (required.length === 0) {
    return { done: 0, total: 0, isComplete: true };
  }

  const items = solicitud.documentacion?.items || [];
  let done = 0;

  for (const req of required) {
    const doc = items.find((d) => d.tipoDocumento === req);
    if (doc) {
      const validado = doc.estadoIA === "VALIDO" ||
        Boolean(doc.validadoManualmentePor);
      if (validado) done++;
    }
  }

  return {
    done,
    total: required.length,
    isComplete: done === required.length,
  };
}

/** True si la doc del cliente y la interna (pre) están listas para emitir. */
export function isDocumentationReadyForEmission(
  solicitud: SolicitudPolizaV3,
  mode?: DocEmbudoMode
): boolean {
  const client = getClientDocProgress(solicitud, mode);
  const internal = getInternalDocProgress(solicitud, mode);
  return client.isComplete && !client.hasIssue && internal.isComplete;
}

export interface PolizaEmitidaBadge {
  label: string;
  emitted: boolean;
}

function formatCompaniaLabel(compania: string): string {
  const upper = compania.toUpperCase();
  if (upper === "ARAG") return "ARAG";
  return upper.charAt(0) + upper.slice(1).toLowerCase();
}

/**
 * Pólizas emitidas por bots/agentes, independientes de la doc del cliente.
 */
export function getPolizasEmitidas(solicitud: SolicitudPolizaV3): PolizaEmitidaBadge[] {
  const items = solicitud.documentacion?.items || [];
  const badges: PolizaEmitidaBadge[] = [];

  const sel = solicitud.seleccionAgente;
  const companiaRC =
    (typeof sel === "object" && sel?.companiaRC) || solicitud.companiaRC;
  const tieneAsistencia =
    typeof sel === "object" && !!sel?.companiaAsistencia;

  if (companiaRC) {
    const rcDoc = items.find(
      (d) =>
        d.tipoDocumento === "Póliza RC" ||
        d.nombre?.toUpperCase().includes("_POLIZA_RC")
    );
    badges.push({
      label: rcDoc
        ? `${formatCompaniaLabel(companiaRC)} emitida`
        : `${formatCompaniaLabel(companiaRC)} pendiente`,
      emitted: !!rcDoc,
    });
  }

  if (tieneAsistencia) {
    const aragDoc = items.find(
      (d) =>
        d.tipoDocumento === "Póliza Asistencia" ||
        d.nombre?.toUpperCase().includes("_POLIZA_ARAG")
    );
    badges.push({
      label: aragDoc ? "ARAG emitida" : "ARAG pendiente",
      emitted: !!aragDoc,
    });
  }

  return badges;
}

/**
 * Resume el mensaje de discrepancia de la IA para mostrarlo al agente.
 * Los mensajes antiguos suelen incluir datos que sí coinciden; aquí nos
 * quedamos solo con el motivo del rechazo o la duda.
 */
export function resumirMensajeDiscrepancia(mensaje?: string): string {
  if (!mensaje?.trim()) {
    return "La IA detectó una discrepancia. Revisa el documento y decide si aceptarlo o reclamarlo al cliente.";
  }

  const texto = mensaje.trim();

  const trasMarcador = texto.split(
    /\b(?:sin embargo|pero|no obstante|discrepancia|no coincide|no coinciden)\b[,:]?\s*/i
  );
  if (trasMarcador.length > 1) {
    const parte = trasMarcador[trasMarcador.length - 1].trim();
    if (parte.length >= 15) return parte.endsWith(".") ? parte : `${parte}.`;
  }

  const sinPrefijoOk = texto
    .replace(
      /^.*?(?:coinciden con (?:los de )?la solicitud|datos del vehículo[^.]*coinciden)[^.]*\.\s*/i,
      ""
    )
    .trim();
  if (sinPrefijoOk.length >= 15 && sinPrefijoOk !== texto) {
    return sinPrefijoOk.endsWith(".") ? sinPrefijoOk : `${sinPrefijoOk}.`;
  }

  return texto;
}

/**
 * True si ya hay un PDF/imagen de la póliza RC adjunto en la
 * documentación de la solicitud (subido a mano o por el bot). Se usa
 * para bloquear la confirmación de emisión de RC si falta el documento,
 * aunque ya se haya introducido el número de póliza.
 */
export function tieneDocumentoPolizaRC(solicitud: SolicitudPolizaV3): boolean {
  const items = solicitud.documentacion?.items || [];
  return items.some(
    (d) =>
      d.tipoDocumento === "Póliza RC" ||
      d.nombre?.toUpperCase().includes("_POLIZA_RC")
  );
}

/**
 * True si ya hay un PDF/imagen de la póliza de Asistencia (ARAG) adjunto,
 * subido a mano o por el bot. Análogo a `tieneDocumentoPolizaRC` pero para
 * la póliza de Asistencia; se usa para exigir el documento antes de marcar
 * la Asistencia como emitida manualmente.
 */
export function tieneDocumentoPolizaAsistencia(solicitud: SolicitudPolizaV3): boolean {
  const items = solicitud.documentacion?.items || [];
  return items.some(
    (d) =>
      d.tipoDocumento === "Póliza Asistencia" ||
      d.nombre?.toUpperCase().includes("_POLIZA_ARAG")
  );
}

// ── Time Helpers ────────────────────────────────────────────────

/**
 * Convierte un valor de fecha (Firestore Timestamp, ISO string, número o
 * Date) a un objeto Date nativo. Devuelve `null` si el valor es nulo.
 */
export function toJsDate(fechaLike: FechaLike | null | undefined): Date | null {
  if (!fechaLike) return null;
  if (typeof fechaLike === "object" && "toDate" in fechaLike) {
    return fechaLike.toDate();
  }
  return new Date(fechaLike);
}

/**
 * Formats a date as a relative time string (e.g. "Hace 5m", "Hace 2h").
 * Handles Firestore Timestamps (.toDate()), ISO strings, and Date objects.
 */
export function timeAgo(dateInput: FechaLike | null | undefined): string {
  const date = toJsDate(dateInput);
  if (!date) return "";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Hace un momento";
  if (diffMins < 60) return `Hace ${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Hace ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `Hace ${diffDays}d`;
}

/**
 * Fecha y hora en que el cliente envió la solicitud en la web
 * (`fechaCreacion`, o `created_at` de MySQL si falta).
 */
export function formatFechaAltaWeb(solicitud: {
  fechaCreacion?: FechaLike;
  created_at?: string;
}): string | null {
  const fecha = toJsDate(solicitud.fechaCreacion) || toJsDate(solicitud.created_at);
  if (!fecha || Number.isNaN(fecha.getTime())) return null;
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Agent Rendering ─────────────────────────────────────────────

/**
 * Renders the seleccionAgente value as a human-readable string.
 */
export function renderAgente(agente: SeleccionAgente | null | undefined): string {
  if (!agente) return "No definido";
  if (typeof agente === "string") return agente;
  const parts: string[] = [];
  if (agente.companiaRC) parts.push(agente.companiaRC);
  if (agente.companiaAsistencia) parts.push(agente.companiaAsistencia);
  return parts.length > 0 ? parts.join(" + ") : "No definido";
}

/**
 * Returns a display string for the compañía assigned to a solicitud.
 */
export function getCompania(solicitud: SolicitudPolizaV3): string | null {
  const sel = solicitud.seleccionAgente;
  if (typeof sel === "object" && sel?.companiaRC) {
    const parts = [sel.companiaRC];
    if (sel.companiaAsistencia) parts.push(sel.companiaAsistencia);
    return parts.join(" + ");
  }
  if (typeof sel === "string" && sel) return sel;
  return null;
}

// ── Kanban State Mapping ──────────────────────────────────────────
//
// `estadoKanban` es la ÚNICA fuente de verdad del estado de una solicitud
// (columna del tablón y fase de negocio a la vez). El antiguo campo
// `estadoActual` ya no se escribe en ningún sitio nuevo: `mapEstadoKanban`
// solo lo consulta como red de seguridad para documentos históricos que se
// crearon antes de esta unificación y a los que aún no se les ha vuelto a
// tocar `estadoKanban`.

/**
 * Devuelve el `estadoKanban` "efectivo" de una solicitud: el campo tal cual
 * si existe, o su equivalente derivado del legacy `estadoActual` si el
 * documento es antiguo y todavía no lo tiene.
 */
export function mapEstadoKanban(solicitud: SolicitudPolizaV3 | null): string {
  if (!solicitud) return "NUEVA_SOLICITUD";
  
  let est = solicitud.estadoKanban;
  if (!est) {
    const ea = solicitud.estadoActual || "";
    if (ea === "PENDIENTE_REVISION") est = "NUEVA_SOLICITUD";
    else if (ea === "FALTA_DOC") est = "PENDIENTE_DOCUMENTACION";
    else if (ea === "REVISION_OK_LISTO") est = "DOCUMENTACION_COMPLETA";
    else if (["EN_PROCESO_EMISION", "PROCESANDO_RPA"].includes(ea)) est = "EN_EMISION_AUTOMATICA";
    else if (ea === "EMISION_MANUAL") est = "EMISION_MANUAL";
    else if (ea === "EMITIDA_PENDIENTE_COBRO") est = "PRE_EMISION";
    else if (ea === "ERROR_EMISION") est = "ERROR_EMISION";
    else if (ea === "ENVIADA_CLIENTE") est = "ENVIADA_CLIENTE";
    else if (ea === "ARCHIVADA") est = "ARCHIVADA";
    else if (ea === "CANCELADA") est = "CANCELADA";
    else if (ea === "EXPIRADA") est = "EXPIRADA";
    else est = "NUEVA_SOLICITUD";
  }

  // Si está como PENDIENTE_DOCUMENTACION pero toda la documentación requerida
  // (cliente + interna en pre-clásicos) ya está validada, la tratamos como
  // DOCUMENTACION_COMPLETA para mostrar "Doc OK" y el botón de aprobar.
  if (est === "PENDIENTE_DOCUMENTACION") {
    if (isDocumentationReadyForEmission(solicitud)) {
      return "DOCUMENTACION_COMPLETA";
    }
  }

  // ENVIO_PENDIENTE es un estado transitorio interno que usa la Cloud
  // Function `enviarPolizasSolicitud` para bloquear envíos duplicados
  // (ver canStartPolicyDelivery en functions/src/solicitudTransitionValidation.ts).
  // Si el envío falla a mitad (p.ej. error de Fast360), la solicitud se
  // queda en ENVIO_PENDIENTE indefinidamente para permitir reintentar sin
  // perder el candado. Ningún filtro/pestaña del frontend conocía este
  // estado, así que la solicitud desaparecía de todas las vistas. Se trata
  // igual que PRE_EMISION (misma columna "Post-Emisión", mismo botón de
  // "Enviar Pólizas" disponible para reintentar).
  if (est === "ENVIO_PENDIENTE") {
    return "PRE_EMISION";
  }

  return est;
}

export type FaseNegocio = "INBOX" | "EN_TRAMITE" | "EMITIDA" | "ERROR" | "ARCHIVADA" | "PAUSADA";

/**
 * Agrupa el `estadoKanban` (efectivo) de una solicitud en una fase de
 * negocio de alto nivel. Es la función que deben usar el dashboard, las
 * métricas, las alertas de SLA y el contador de la barra lateral en vez de
 * comparar strings de estado sueltos: así toda la app comparte el mismo
 * criterio de "qué cuenta como en trámite / emitida / etc."
 */
export function getFaseNegocio(solicitud: SolicitudPolizaV3): FaseNegocio {
  switch (mapEstadoKanban(solicitud)) {
    case "NUEVA_SOLICITUD":
    case "PENDIENTE_DOCUMENTACION":
    case "DOCUMENTACION_COMPLETA":
      return "INBOX";
    case "EN_EMISION_AUTOMATICA":
    case "EMISION_MANUAL":
      return "EN_TRAMITE";
    case "ERROR_EMISION":
      return "ERROR";
    case "PRE_EMISION":
    case "POLIZA_LISTA":
    case "PENDIENTE_COBRO":
    case "ENVIADA_CLIENTE":
    case "COMPLETADA":
      return "EMITIDA";
    case "ARCHIVADA":
      return "ARCHIVADA";
    default:
      // SNOOZE, CANCELADA, EXPIRADA: pausadas o cerradas, fuera de los
      // conteos activos (bandeja/trámite/emitidas/errores) y de las
      // alertas de SLA a propósito.
      return "PAUSADA";
  }
}

/**
 * Returns a short human label for the Kanban status (for MiniCard badges).
 */
export function getStatusLabel(solicitud: SolicitudPolizaV3): string {
  const ek = mapEstadoKanban(solicitud);
  if (ek === "ERROR_EMISION") return "Error";
  if (ek === "EN_EMISION_AUTOMATICA" || ek === "EMISION_MANUAL") return "Emitiendo";
  if (ek === "PENDIENTE_COBRO") return "Pendiente cobro";
  if (ek === "POLIZA_LISTA") return "Lista para envío";
  if (ek === "PRE_EMISION") {
    const solAny = asRawRecord(solicitud);
    if (solAny.estadoKanban === "ENVIO_PENDIENTE") {
      return solAny.envioPolizasEstado === "ERROR" ? "Error al enviar" : "Enviando...";
    }
    return "Post-emisión";
  }
  if (ek === "ENVIADA_CLIENTE" || ek === "COMPLETADA") return "Emitida";
  if (ek === "ARCHIVADA") return "Archivada";
  if (ek === "SNOOZE") return "Snooze";
  if (ek === "DOCUMENTACION_COMPLETA") return "Doc OK";
  if (ek === "PENDIENTE_DOCUMENTACION") {
    const client = getClientDocProgress(solicitud);
    const internal = getInternalDocProgress(solicitud);
    if (client.isComplete && !client.hasIssue && internal.total > 0 && !internal.isComplete) {
      return "Falta cert. Club";
    }
    return "Falta Doc";
  }
  if (ek === "CANCELADA") return "Cancelada";
  if (ek === "EXPIRADA") return "Expirada";
  if (ek === "NUEVA_SOLICITUD") return "Nueva";
  return "Pendiente";
}

// ── Display Helpers (Fallback for historical data) ──────────────

export function cleanMatricula(raw?: string): string {
  if (!raw) return "";
  return raw.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

function asRawRecord(solicitud: SolicitudPolizaV3): Record<string, unknown> {
  return solicitud as unknown as Record<string, unknown>;
}

function rawStr(record: Record<string, unknown>, key: string): string {
  const val = record[key];
  return typeof val === "string" ? val : val != null ? String(val) : "";
}

export function isMotoVehicle(solicitud: SolicitudPolizaV3): boolean {
  const s = asRawRecord(solicitud);
  return (rawStr(s, "vehiculo_tipo") || rawStr(s, "tipo_vehiculo")).toLowerCase().includes("moto");
}

export function getDisplayMarca(solicitud: SolicitudPolizaV3): string {
  const s = asRawRecord(solicitud);
  return rawStr(s, "vehiculo_marca_text") || rawStr(s, "marca") || rawStr(s, "vehiculo_marca");
}

export function getDisplayModelo(solicitud: SolicitudPolizaV3): string {
  const s = asRawRecord(solicitud);
  return rawStr(s, "vehiculo_modelo_text") || rawStr(s, "modelo") || rawStr(s, "vehiculo_modelo");
}

export function getDriverFullName(solicitud: SolicitudPolizaV3): string {
  const tNombre = solicitud.conductor_nombre || "";
  const tApellidos = solicitud.conductor_apellidos || "";
  const name = `${tNombre} ${tApellidos}`.trim();
  if (name) return name;
  // Fallback si no hay desglosado
  return (solicitud as any).conductor_nombre_completo || "S/D";
}

export function getOwnerFullName(solicitud: SolicitudPolizaV3): string {
  const pNombre = (solicitud as any).propietario_nombre || "";
  const pApellidos = (solicitud as any).propietario_apellidos || "";
  const name = `${pNombre} ${pApellidos}`.trim();
  if (name) return name;
  return (solicitud as any).propietario_nombre_completo || "S/D";
}

export function hasDifferentOwner(solicitud: SolicitudPolizaV3): boolean {
  if ((solicitud as any).tomador_es_propietario === false) return true;
  if ((solicitud as any).conductor_es_propietario === false) return true;
  
  const dDNI = solicitud.conductor_numero_documento?.trim().toUpperCase();
  const pDNI = (solicitud as any).propietario_numero_documento?.trim().toUpperCase();
  
  if (pDNI && dDNI && pDNI !== dDNI) return true;
  return false;
}

// ── Urgency / Visual Helpers ────────────────────────────────────

/**
 * Computes urgency-based colors from the policy's efecto date.
 */
export function getUrgencyColor(solicitud: SolicitudPolizaV3): { bg: string; dot: string; dotPulse?: boolean } {
  const solAny = asRawRecord(solicitud);
  if (solicitud.estadoKanban === "ARCHIVADA") {
    return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 border-l-[3px] border-l-emerald-500", dot: "bg-emerald-500" };
  }
  if (solicitud.estadoKanban === "CANCELADA") {
    return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 border-l-[3px] border-l-slate-400", dot: "bg-slate-500" };
  }

  const rawDate = solAny.fecha_cobertura || solAny.fechaCobertura || solAny.fechaEfecto || solAny.fecha_efecto || solicitud.efecto_poliza;
  
  if (!rawDate) return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800", dot: "bg-slate-400" };
  
  const dateStr = typeof rawDate === "object" && rawDate !== null && "seconds" in rawDate
    ? new Date((rawDate as { seconds: number }).seconds * 1000).toISOString() : String(rawDate);
  const fecha = new Date(dateStr);
  if (isNaN(fecha.getTime())) return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800", dot: "bg-slate-400" };

  fecha.setHours(0, 0, 0, 0);
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((fecha.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 border-l-[3px] border-l-red-500 dark:border-l-red-500", dot: "bg-red-500", dotPulse: true };
  if (diffDays === 0) return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 border-l-[3px] border-l-amber-500 dark:border-l-amber-500", dot: "bg-amber-500", dotPulse: true };
  if (diffDays <= 3) return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 border-l-[3px] border-l-emerald-500 dark:border-l-emerald-500", dot: "bg-emerald-500" };
  if (diffDays <= 7) return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 border-l-[3px] border-l-blue-500 dark:border-l-blue-500", dot: "bg-blue-500" };
  return { bg: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800", dot: "bg-slate-400" };
}

// ── Cobro con Tarjeta (bloqueo de fondos con caducidad) ─────────
//
// Cuando la solicitud llega con forma de pago "tarjeta" (tipo_pago = "TA" o
// "tarjeta"), la pasarela retiene el importe en la tarjeta del cliente
// durante una ventana de tiempo limitada desde que la solicitud entra en
// MySQL (fechaCreacion). Si el agente no valida el cobro (y se envían las
// pólizas) antes de que esa ventana caduque, el bloqueo del dinero se
// libera y hay que volver a cobrar. Este helper es la única fuente de
// verdad de "cuánto tiempo queda": lo usan el badge de las tarjetas del
// Kanban, el panel de detalle, el orden de la columna Post-Emisión y el
// cron de vigilancia en el backend (mantener en sync con
// `functions/src/cronjobs.ts`).

/** Ventana de caducidad del bloqueo de fondos con tarjeta, en días. */
export const VENTANA_COBRO_TARJETA_DIAS = 7;

/** True si la solicitud entró con forma de pago tarjeta (cualquiera de los dos formatos observados en MySQL). */
export function esPagoTarjeta(solicitud: SolicitudPolizaV3 | null | undefined): boolean {
  const tipoPago = (solicitud as unknown as Record<string, unknown>)?.tipo_pago;
  if (!tipoPago || typeof tipoPago !== "string") return false;
  return tipoPago === "TA" || tipoPago.toLowerCase() === "tarjeta";
}

/**
 * Estados de Kanban en los que la solicitud ya salió del circuito de
 * cobro: o bien se canceló/expiró/archivó, o bien ya se envió al cliente
 * (lo que implica que el cobro se validó en su momento). A partir de aquí
 * ya no tiene sentido seguir mostrando la cuenta atrás. En cualquier OTRO
 * estado (incluida la Bandeja o Emisión) la cuenta atrás debe verse, ya
 * que el TPV del banco empieza a contar en cuanto el cliente envía la
 * solicitud, no cuando el agente llega a Post-Emisión.
 */
const ESTADOS_TERMINALES_COBRO_TARJETA = ["CANCELADA", "EXPIRADA", "ARCHIVADA", "ENVIADA_CLIENTE", "COMPLETADA"];

export interface CardPaymentDeadlineInfo {
  /** Horas restantes hasta la caducidad (negativas si ya caducó). */
  horasRestantes: number;
  /** Días restantes redondeados hacia arriba (para mostrar "3d restantes"), 0 si ya caducó. */
  diasRestantes: number;
  /** true si ya han pasado los VENTANA_COBRO_TARJETA_DIAS días. */
  vencido: boolean;
  /** true si quedan 48h o menos (y aún no ha caducado): momento de escalar visualmente. */
  urgente: boolean;
  /** Texto corto listo para mostrar en un badge. */
  label: string;
  /** Nivel de severidad para colorear el badge, consistente con el resto de la UI (verde → ámbar → rojo). */
  severity: "ok" | "amber" | "red" | "vencido";
}

/**
 * Calcula cuánto queda del bloqueo de fondos de una solicitud pagada con
 * tarjeta, o `null` si no aplica (no es pago con tarjeta, ya se validó el
 * cobro, o la solicitud ya salió del circuito de cobro). El ancla es
 * siempre `fechaCreacion` (o su fallback), es decir el momento en que el
 * cliente envió la solicitud: ese es el instante real en que el TPV del
 * banco retiene el importe, independientemente de en qué columna del
 * Kanban esté la solicitud.
 */
export function getCardPaymentDeadlineInfo(
  solicitud: SolicitudPolizaV3 | null | undefined,
  maxDays: number = VENTANA_COBRO_TARJETA_DIAS
): CardPaymentDeadlineInfo | null {
  if (!solicitud) return null;
  if (!esPagoTarjeta(solicitud)) return null;
  if ((solicitud as unknown as Record<string, unknown>).validadoPor) return null;
  if (ESTADOS_TERMINALES_COBRO_TARJETA.includes(mapEstadoKanban(solicitud))) return null;

  const solAny = solicitud as unknown as Record<string, unknown>;
  const fechaBase =
    toJsDate(solicitud.fechaCreacion) ||
    toJsDate(solAny.created_at as FechaLike | undefined) ||
    toJsDate(solAny.updated_at as FechaLike | undefined);
  if (!fechaBase) return null;

  const msTotal = maxDays * 24 * 60 * 60 * 1000;
  const msRestantes = fechaBase.getTime() + msTotal - Date.now();
  const horasRestantes = msRestantes / (1000 * 60 * 60);
  const vencido = horasRestantes <= 0;
  const diasRestantes = vencido ? 0 : Math.ceil(horasRestantes / 24);
  const urgente = !vencido && horasRestantes <= 48;

  let severity: CardPaymentDeadlineInfo["severity"];
  let label: string;
  if (vencido) {
    severity = "vencido";
    label = "Bloqueo caducado";
  } else if (horasRestantes <= 24) {
    severity = "red";
    label = `${Math.ceil(horasRestantes)}h restantes`;
  } else if (urgente) {
    severity = "red";
    label = `${diasRestantes}d restantes`;
  } else if (horasRestantes <= 96) {
    severity = "amber";
    label = `${diasRestantes}d restantes`;
  } else {
    severity = "ok";
    label = `${diasRestantes}d restantes`;
  }

  return { horasRestantes, diasRestantes, vencido, urgente, label, severity };
}

// ── Reclamación docs Pre-Clásicos (plazo 15 días) ────────────────

export const PLAZO_RECLAMACION_PRECLASICO_DIAS = 15;

export interface ReclamacionDocsDeadlineInfo {
  horasRestantes: number;
  diasRestantes: number;
  vencido: boolean;
  urgente: boolean;
  pausada: boolean;
  fechaLimiteLabel: string;
  label: string;
  severity: "ok" | "amber" | "red" | "vencido" | "pausada";
}

/**
 * Cuenta atrás del plazo de reclamación Pre-Clásicos.
 * Solo aplica si `reclamacionDocs.activa`. Si está en Snooze, el reloj
 * aparece como pausado.
 */
export function getReclamacionDocsDeadlineInfo(
  solicitud: SolicitudPolizaV3 | null | undefined
): ReclamacionDocsDeadlineInfo | null {
  if (!solicitud?.reclamacionDocs?.activa) return null;
  const r = solicitud.reclamacionDocs;

  if (r.pausadaPorSnooze) {
    const ms = Number(r.msRestantesAlPausar) || 0;
    const dias = Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    return {
      horasRestantes: ms / (1000 * 60 * 60),
      diasRestantes: dias,
      vencido: false,
      urgente: false,
      pausada: true,
      fechaLimiteLabel: "pausada",
      label: dias > 0 ? `Pausada (${dias}d restantes)` : "Pausada (Snooze)",
      severity: "pausada",
    };
  }

  if (!r.fechaLimite) return null;
  const lim = new Date(r.fechaLimite);
  if (Number.isNaN(lim.getTime())) return null;

  const msRestantes = lim.getTime() - Date.now();
  const horasRestantes = msRestantes / (1000 * 60 * 60);
  const vencido = horasRestantes <= 0;
  const diasRestantes = vencido ? 0 : Math.ceil(horasRestantes / 24);
  const urgente = !vencido && diasRestantes <= 2;

  const dd = String(lim.getDate()).padStart(2, "0");
  const mm = String(lim.getMonth() + 1).padStart(2, "0");
  const fechaLimiteLabel = `${dd}/${mm}`;

  let severity: ReclamacionDocsDeadlineInfo["severity"];
  let label: string;
  if (vencido) {
    severity = "vencido";
    label = "Plazo vencido";
  } else if (diasRestantes <= 2) {
    severity = "red";
    label = `${diasRestantes}d · vence ${fechaLimiteLabel}`;
  } else if (diasRestantes <= 7) {
    severity = "amber";
    label = `${diasRestantes}d · vence ${fechaLimiteLabel}`;
  } else {
    severity = "ok";
    label = `${diasRestantes}d · vence ${fechaLimiteLabel}`;
  }

  return {
    horasRestantes,
    diasRestantes,
    vencido,
    urgente,
    pausada: false,
    fechaLimiteLabel,
    label,
    severity,
  };
}

/** Filtros del tablero Pre-Clásicos para el proceso de reclamación. */
export type FiltroReclamacionPre =
  | "todas"
  | "sin_reclamacion"
  | "activa"
  | "vencen_3d";

export function matchFiltroReclamacionPre(
  solicitud: SolicitudPolizaV3,
  filtro: FiltroReclamacionPre
): boolean {
  if (filtro === "todas") return true;
  const r = solicitud.reclamacionDocs;
  if (filtro === "sin_reclamacion") return !r?.activa;
  if (filtro === "activa") return !!r?.activa && !r.pausadaPorSnooze;
  if (filtro === "vencen_3d") {
    const info = getReclamacionDocsDeadlineInfo(solicitud);
    return !!info && !info.pausada && !info.vencido && info.diasRestantes <= 3;
  }
  return true;
}

// ── Fecha Efecto ────────────────────────────────────────────────

/**
 * Parses the efecto date from any of the possible fields and returns
 * formatted info plus urgency flags.
 */
export function getEfectoInfo(solicitud: SolicitudPolizaV3): {
  raw: string | null;
  formatted: string | null;
  date: Date | null;
  isUrgent: boolean;
  isToday: boolean;
} {
  const solAny = asRawRecord(solicitud);
  let rawValue = solAny.fecha_cobertura || solAny.fechaCobertura || solAny.fechaEfecto || solAny.fecha_efecto || solicitud.efecto_poliza || solAny.fechaEfectoRequerida || solAny.efecto;
  if (!rawValue) {
    const keys = Object.keys(solAny);
    for (const key of keys) {
      if (key.toLowerCase().includes('efecto') && solAny[key]) {
        rawValue = solAny[key];
        break;
      }
    }
  }
  if (!rawValue) return { raw: null, formatted: null, date: null, isUrgent: false, isToday: false };

  const rawValueStr = String(rawValue);
  const dateStr = typeof rawValue === "object" && rawValue !== null && "seconds" in rawValue
    ? new Date((rawValue as { seconds: number }).seconds * 1000).toISOString()
    : rawValueStr;

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return { raw: rawValueStr, formatted: rawValueStr, date: null, isUrgent: false, isToday: false };

  const dd = date.getDate().toString().padStart(2, '0');
  const mm = (date.getMonth() + 1).toString().padStart(2, '0');
  const yyyy = date.getFullYear();
  const formatted = `${dd}/${mm}/${yyyy}`;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const isUrgent = date.getTime() <= today.getTime();
  const isToday = date.getTime() === today.getTime();

  return { raw: rawValueStr, formatted, date, isUrgent, isToday };
}

/**
 * Replica en el frontend la lógica de `resolverFechaEfecto` de
 * `src/utils/workerHelpers.ts` (los workers RPA de Zurich/ARAG), para poder
 * guardar `fechaEfectoUsadaRC` también cuando la RC se confirma manualmente
 * (no solo cuando la emite el bot). Debe mantenerse en sync con esa función:
 * misma prioridad de campos y misma regla de "si la fecha deseada ya venció,
 * usar hoy" (las aseguradoras rechazan fechas de efecto retroactivas). Sin
 * esto, la guarda de consistencia de fechas entre RC y ARAG
 * (`verificarConsistenciaFechaEfecto`) no puede detectar un desajuste cuando
 * la RC se emitió a mano.
 */
export function resolverFechaEfectoISO(solicitud: SolicitudPolizaV3 | null | undefined): string {
  const sol = solicitud ? asRawRecord(solicitud) : {};
  const fechaDeseadaStr = sol.fecha_cobertura || sol.efecto_poliza || sol.fechaEfectoRequerida || sol.fechaEfecto;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  let fecha = hoy;
  if (fechaDeseadaStr) {
    const parsed = new Date(String(fechaDeseadaStr));
    if (!isNaN(parsed.getTime())) {
      parsed.setHours(0, 0, 0, 0);
      if (parsed > hoy) {
        fecha = parsed;
      }
    }
  }

  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-${String(fecha.getDate()).padStart(2, "0")}`;
}

// ── Raw Data Helpers (for detail tabs) ──────────────────────────

/** Fields to exclude from the "raw data" display */
export const CAMPOS_OCULTOS_RAW = [
  'logs', 'estadoActual', 'comentarios', 'clavesBusqueda', 'tags',
  'cumple_requisitos', 'tarjeta_numero', 'uuid', 'step', 'como_conocido',
  'asistencia_carretera', 'tarjeta_caducidad', 'requisitos', 'edad_vehiculo',
  'conductor_es_propietario', 'onductor_es_propietario', 'aceptado_condiciones',
  'estado', 'fechaCreacion', 'id', 'user_fingerprint', 'phone_verified', 'auto_confirmacion',
  'updated_at', 'created_at', 'idSolicitud', 'link_pago_tarjeta'
];

/**
 * Campos que el editor manual (SolicitudDetailPanel, botón "Editar Datos")
 * puede guardar mediante la acción ACTUALIZAR_DATOS. Debe coincidir
 * exactamente con DATOS_EDITABLES en functions/src/solicitudMutations.ts:
 * al ser dos proyectos npm distintos no se puede compartir la constante en
 * tiempo de ejecución, así que hay que mantenerlas sincronizadas a mano.
 * Un campo raw que no esté aquí se muestra en modo edición como solo
 * lectura (en vez de un Input que fallaría con 400 al guardar), porque el
 * guardado es todo-o-nada: si se envía un solo campo no permitido, el
 * backend rechaza todos los cambios del lote. Se excluyen a propósito los
 * campos económicos (precio_total, precio, tarifa, primas...), que se
 * editan por su propio flujo ("Editar Pólizas" / ACTUALIZAR_POLIZAS) con
 * validación numérica.
 */
export const CAMPOS_EDITABLES_MANUALMENTE = new Set([
  'conductor_nombre', 'nombre',
  'conductor_primer_apellido', 'conductor_segundo_apellido',
  'conductor_apellidos', 'apellidos',
  'conductor_numero_documento', 'dni',
  'conductor_fecha_nacimiento', 'conductor_fecha_carnet',
  'conductor_direccion_tipo_via', 'conductor_direccion_calle',
  'conductor_direccion_numero', 'conductor_direccion_piso',
  'conductor_direccion_puerta', 'conductor_direccion_escalera',
  'conductor_direccion_cp', 'conductor_direccion_ciudad',
  'conductor_direccion_provincia', 'email', 'telefono',
  'propietario_nombre',
  'propietario_primer_apellido', 'propietario_segundo_apellido',
  'propietario_numero_documento', 'propietario_fecha_nacimiento',
  'propietario_nacionalidad', 'propietario_genero',
  'propietario_direccion_tipo_via', 'propietario_direccion_calle',
  'propietario_direccion_numero', 'propietario_direccion_piso',
  'propietario_direccion_puerta', 'propietario_direccion_escalera',
  'propietario_direccion_cp', 'propietario_direccion_ciudad',
  'propietario_direccion_provincia',
  'vehiculo_matricula', 'matricula',
  'vehiculo_marca_text', 'vehiculo_marca', 'marca',
  'vehiculo_modelo_text', 'vehiculo_modelo', 'modelo',
  'vehiculo_version', 'tipo_vehiculo', 'vehiculo_tipo',
  'vehiculo_fecha_matriculacion', 'vehiculo_ano', 'vehiculo_combustible',
  'fecha_cobertura', 'efecto_poliza',
  'cobertura_solicitada', 'cobertura', 'coberturas', 'tipo_seguro',
]);

/** Ordered fields grouped by section for the raw data tab */
export const ORDEN_CAMPOS: Record<string, string[]> = {
  'Tomador / Conductor': [
    'conductor_nombre', 'nombre', 'conductor_primer_apellido', 'conductor_segundo_apellido', 'conductor_apellidos', 'apellidos',
    'conductor_numero_documento', 'dni', 'conductor_fecha_nacimiento', 'conductor_fecha_carnet',
    'conductor_direccion_tipo_via', 'conductor_direccion_calle', 'conductor_direccion_numero', 'conductor_direccion_piso', 'conductor_direccion_puerta', 'conductor_direccion_escalera', 'conductor_direccion_cp', 'conductor_direccion_ciudad', 'conductor_direccion_provincia',
    'email', 'telefono'
  ],
  'Propietario': [
    'propietario_nombre', 'propietario_primer_apellido', 'propietario_segundo_apellido',
    'propietario_numero_documento', 'propietario_fecha_nacimiento', 'propietario_nacionalidad', 'propietario_genero',
    'propietario_direccion_tipo_via', 'propietario_direccion_calle', 'propietario_direccion_numero', 'propietario_direccion_piso', 'propietario_direccion_puerta', 'propietario_direccion_escalera', 'propietario_direccion_cp', 'propietario_direccion_ciudad', 'propietario_direccion_provincia'
  ],
  'Vehículo': [
    'vehiculo_matricula', 'matricula', 'vehiculo_marca_text', 'vehiculo_marca', 'vehiculo_modelo_text', 'vehiculo_modelo', 'vehiculo_version', 'tipo_vehiculo', 'vehiculo_tipo',
    'vehiculo_fecha_matriculacion', 'vehiculo_ano', 'vehiculo_franja_antiguedad', 'vehiculo_combustible'
  ],
  'Datos Póliza': [
    'precio_total', 'precio', 'tarjeta_token', 'tarifa', 'efecto_poliza', 'fechaEfecto', 'cobertura_solicitada', 'cobertura', 'tipo_seguro'
  ]
};

export function getDisplayCobertura(solicitud: SolicitudPolizaV3 | Record<string, unknown>): string {
  const s = solicitud as Record<string, unknown>;
  const val = s.cobertura_solicitada || s.coberturaSolicitada || s.cobertura || s.coberturas || s.tipo_seguro || s.tipoSeguro || s.seguro_cobertura || s.modalidad || s.modalidad_seguro || s.garantias || s.ramo || s.producto;
  if (!val || typeof val !== "string" || val.trim() === "") return "Cobertura n/d";
  return val;
}

export type PlanCobertura = "basic" | "completo";

/** Normaliza el plan de cobertura a basic/completo (misma lógica que Fast360). */
export function getPlanCobertura(solicitud: SolicitudPolizaV3 | Record<string, unknown> | null | undefined): PlanCobertura {
  const s = solicitud as Record<string, unknown> | null | undefined;
  if (s?.tipoVehiculoKanban === "MOTO_COMPLETO") return "completo";
  if (s?.tipoVehiculoKanban === "MOTO_BASICO") return "basic";

  const raw = getDisplayCobertura(solicitud || {}).toLowerCase().trim();
  if (raw === "cobertura n/d") return "basic";

  if (raw.includes("complet") || raw.includes("todo") || raw.includes("ampliad")) {
    return "completo";
  }

  return "basic";
}

/** Las motos con plan Basic no incluyen asistencia en carretera (ARAG). */
export function aplicaAsistenciaARAG(solicitud: SolicitudPolizaV3 | null | undefined): boolean {
  if (!solicitud) return true;
  if (!isMotoVehicle(solicitud)) return true;
  return getPlanCobertura(solicitud) !== "basic";
}

// ── Separación Comentarios (humanos) vs Actividad (técnica/sistema) ────
//
// Históricamente el array `comentarios` mezclaba notas escritas a mano por
// agentes con avisos automáticos del sistema (OCR, cronjobs, sync FTP/IMAP,
// etc.), identificables porque su `nombreUsuario` lleva un emoji/prefijo de
// robot y casi nunca tienen `uid` (los comentarios humanos siempre lo
// llevan, ver ComentariosPanel). A partir de ahora los eventos nuevos de
// sistema se escriben en `logs`, pero este filtro se mantiene para no tener
// que migrar el histórico ya guardado en `comentarios`.
const PATRON_NOMBRE_SISTEMA = /^(🤖|⏰|⏳|🧹|⚠️|👁️)|sistema|ia validadora/i;

/** True si un comentario fue generado automáticamente (no por un agente humano). */
export function esComentarioSistema(c: Comentario | null | undefined): boolean {
  if (!c) return true;
  if (!c.uid) return true;
  return PATRON_NOMBRE_SISTEMA.test(c.nombreUsuario || "");
}

/** Comentarios realmente escritos por agentes (lo que ve un usuario normal). */
export function getComentariosHumanos(solicitud: SolicitudPolizaV3 | null | undefined): Comentario[] {
  return (solicitud?.comentarios || []).filter((c) => !esComentarioSistema(c));
}

/** Último comentario humano (para previews en tarjetas/dashboard). */
export function getUltimoComentarioHumano(solicitud: SolicitudPolizaV3 | null | undefined): Comentario | null {
  const humanos = getComentariosHumanos(solicitud);
  return humanos.length > 0 ? humanos[humanos.length - 1] : null;
}

/** Determines which group a raw key belongs to */
export function getRawGroup(key: string): string {
  if (key.startsWith('conductor_') || key === 'dni' || key === 'email' || key === 'telefono' || key === 'nombre' || key === 'apellidos') return 'Tomador / Conductor';
  if (key.startsWith('propietario_')) return 'Propietario';
  if (key.startsWith('vehiculo_') || key === 'marca' || key === 'modelo' || key === 'matricula' || key === 'tipo_vehiculo') return 'Vehículo';
  return 'Datos Póliza';
}

/** Formats a raw MySQL field name into a human-readable label */
export function formatRawLabel(key: string): string {
  return key
    .replace(/^(conductor_|propietario_|vehiculo_)/, '')
    .replace(/_text$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}
